import React, { useState, useRef, useEffect } from 'react';
import { api, formatTime, formatDateTime } from '../api';

// Fungsi overlay timestamp + GPS ke foto
function addOverlayToCanvas(source, lat, lng) {
  const canvas = document.createElement('canvas');
  canvas.width = source.videoWidth || source.width || source.naturalWidth;
  canvas.height = source.videoHeight || source.height || source.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0);
  const now = new Date();
  const timestamp = now.toLocaleString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const gpsText = lat && lng ? `GPS: ${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}` : 'GPS: -';
  const barHeight = 52;
  ctx.fillStyle = 'rgba(0,0,0,0.72)';
  ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);
  ctx.fillStyle = '#c9a84c';
  ctx.font = `bold ${Math.max(14, canvas.width / 32)}px Arial`;
  ctx.fillText(`MIG Security · ${timestamp}`, 10, canvas.height - barHeight + 20);
  ctx.fillStyle = '#fff';
  ctx.font = `${Math.max(12, canvas.width / 38)}px Arial`;
  ctx.fillText(gpsText, 10, canvas.height - barHeight + 40);
  return canvas;
}

// Hitung jarak 2 koordinat dalam meter
function hitungJarak(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// ─── REKAP VIEW (Admin/SPV) ────────────────────────────────────────────────
function RekapAbsensi({ user }) {
  const [rekap, setRekap] = useState([]);
  const [wilayah, setWilayah] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    bulan: new Date().getMonth() + 1,
    tahun: new Date().getFullYear(),
    wilayah_id: ''
  });
  const [absenDetail, setAbsenDetail] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { fetchRekap(); }, [filter]);
  useEffect(() => {
    api.get('/wilayah').then(setWilayah).catch(() => {});
  }, []);

  const fetchRekap = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        bulan: filter.bulan,
        tahun: filter.tahun,
        ...(filter.wilayah_id && { wilayah_id: filter.wilayah_id })
      });
      const data = await api.get(`/absensi/rekap?${params}`);
      setRekap(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (anggotaId) => {
    try {
      const params = new URLSearchParams({
        anggota_id: anggotaId,
        bulan: filter.bulan,
        tahun: filter.tahun,
        ...(filter.wilayah_id && { wilayah_id: filter.wilayah_id })
      });
      const data = await api.get(`/absensi?${params}`);
      setAbsenDetail(data);
    } catch {}
  };

  const downloadCSV = () => {
    const bulanNama = new Date(filter.tahun, filter.bulan - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
    const headers = ['Nama', 'Total Masuk', 'Total Keluar', 'Total Izin', 'Total Alpha'];
    const rows = rekap.map(r => [r.name, r.total_masuk, r.total_keluar, r.total_izin, r.total_alpha]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rekap_absensi_${bulanNama.replace(' ', '_')}.csv`;
    a.click();
  };

  const downloadLaporanAnggota = async (anggota) => {
    try {
      const data = await api.get(`/absensi/laporan-anggota?anggota_id=${anggota.id}&bulan=${filter.bulan}&tahun=${filter.tahun}`);
      const bulanNama = new Date(filter.tahun, filter.bulan - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });

      // Generate tabel absensi per hari
      const daysInMonth = new Date(filter.tahun, filter.bulan, 0).getDate();
      let absenRows = '';
      let totalHadir = 0, totalIzin = 0, totalAlpha = 0, totalMenit = 0;

      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${filter.tahun}-${String(filter.bulan).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const dayData = data.absen_by_date[dateStr];
        const dayName = new Date(dateStr + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'short' });
        let masukStr = '-', keluarStr = '-', durasiStr = '-', statusStr = 'Tidak Ada Data', statusColor = '#888';

        if (dayData) {
          if (dayData.masuk) { masukStr = new Date(dayData.masuk).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }); totalHadir++; statusStr = 'Hadir'; statusColor = '#2e7d32'; }
          if (dayData.keluar) { keluarStr = new Date(dayData.keluar).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }); }
          if (dayData.masuk && dayData.keluar) {
            const menit = Math.round((new Date(dayData.keluar) - new Date(dayData.masuk)) / 60000);
            totalMenit += menit;
            const jam = Math.floor(menit / 60); const sisa = menit % 60;
            durasiStr = sisa > 0 ? `${jam}j ${sisa}m` : `${jam}j`;
          }
          if (dayData.izin) { masukStr = '-'; keluarStr = '-'; durasiStr = '-'; statusStr = 'Izin'; statusColor = '#e65100'; totalIzin++; }
          if (dayData.alpha) { masukStr = '-'; keluarStr = '-'; durasiStr = '-'; statusStr = 'Alpha'; statusColor = '#c62828'; totalAlpha++; }
        }

        const isWeekend = new Date(dateStr + 'T00:00:00').getDay() === 0 || new Date(dateStr + 'T00:00:00').getDay() === 6;
        absenRows += `<tr style="background:${isWeekend ? '#f5f5f5' : 'white'}">
          <td style="text-align:center">${d}</td>
          <td style="text-align:center;color:#888;font-size:11px">${dayName}</td>
          <td style="text-align:center;font-family:monospace">${masukStr}</td>
          <td style="text-align:center;font-family:monospace">${keluarStr}</td>
          <td style="text-align:center">${durasiStr}</td>
          <td style="text-align:center"><span style="color:${statusColor};font-weight:600;font-size:11px">${statusStr}</span></td>
        </tr>`;
      }

      const totalJam = Math.floor(totalMenit / 60);
      const totalSisa = totalMenit % 60;
      const totalDurasi = totalMenit > 0 ? `${totalJam} jam ${totalSisa} menit` : '-';

      // Generate laporan kejadian (halaman terpisah)
      let laporanSection = '';
      if (data.laporan && data.laporan.length > 0) {
        const laporanItems = data.laporan.map(l => `
          <div style="border:1px solid #ddd;border-radius:8px;padding:14px;margin-bottom:12px;page-break-inside:avoid">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
              <div>
                <div style="font-weight:700;font-size:14px">${l.judul}</div>
                <div style="font-size:11px;color:#666;margin-top:2px">${new Date(l.created_at).toLocaleDateString('id-ID', {day:'numeric',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})} · ${l.kategori}</div>
              </div>
              <span style="padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${l.status==='selesai'?'#e8f5e9':l.status==='diproses'?'#fff3e0':'#e3f2fd'};color:${l.status==='selesai'?'#2e7d32':l.status==='diproses'?'#e65100':'#1565c0'}">${l.status}</span>
            </div>
            <p style="font-size:13px;line-height:1.6;margin:0 0 8px;color:#333">${l.isi}</p>
            ${l.sla_menit ? `<div style="font-size:11px;color:#666">⏱ SLA: ${Math.floor(l.sla_menit/60)}j ${l.sla_menit%60}m${l.selesai_by_name ? ` · Diselesaikan oleh: <strong>${l.selesai_by_name}</strong>` : ''}${l.diproses_by_name ? ` · Diproses oleh: <strong>${l.diproses_by_name}</strong>` : ''}</div>` : ''}
          </div>`).join('');

        laporanSection = `
          <div style="page-break-before:always">
            <div style="border-bottom:3px solid #c9a84c;padding-bottom:12px;margin-bottom:20px">
              <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.05em">Lampiran</div>
              <h2 style="margin:4px 0 0;font-size:18px">Laporan Kejadian</h2>
              <div style="font-size:12px;color:#666">${data.anggota.name} · ${bulanNama} · ${data.laporan.length} laporan</div>
            </div>
            ${laporanItems}
          </div>`;
      }

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Laporan ${data.anggota.name} - ${bulanNama}</title>
        <style>
          body{font-family:Arial,sans-serif;margin:30px;color:#1a1a1a;font-size:13px}
          table{width:100%;border-collapse:collapse}
          th{background:#0b1829;color:#c9a84c;padding:8px;text-align:center;font-size:11px;font-weight:600}
          td{padding:6px 8px;border-bottom:1px solid #eee;font-size:12px}
          .header{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:3px solid #c9a84c;padding-bottom:16px;margin-bottom:20px}
          .logo{font-size:24px;font-weight:900;color:#0b1829}
          .logo span{color:#c9a84c}
          .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}
          .sum-card{background:#f9f9f9;padding:10px;border-radius:8px;text-align:center}
          .sum-val{font-size:22px;font-weight:800;color:#0b1829}
          .sum-lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:0.05em}
          @media print{body{margin:0}}
        </style></head><body>
        <div class="header">
          <div>
            <div class="logo"><span>MIG</span> Security</div>
            <div style="font-size:11px;color:#888">PT. Muhammad Ibrahim Group</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:16px;font-weight:700">Laporan Kehadiran</div>
            <div style="font-size:12px;color:#666">${bulanNama}</div>
            <div style="font-size:11px;color:#888;margin-top:4px">Dicetak: ${new Date().toLocaleString('id-ID')}</div>
          </div>
        </div>

        <div style="display:flex;gap:16px;align-items:center;margin-bottom:16px">
          <div style="width:56px;height:56px;border-radius:12px;background:#0b1829;display:flex;align-items:center;justify-content:center;color:#c9a84c;font-size:22px;font-weight:800;flex-shrink:0">
            ${data.anggota.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style="font-size:18px;font-weight:800">${data.anggota.name}</div>
            <div style="font-size:12px;color:#666">${data.anggota.email}${data.anggota.wilayah_nama ? ` · 📍 ${data.anggota.wilayah_nama}` : ''}</div>
          </div>
        </div>

        <div class="summary">
          <div class="sum-card"><div class="sum-val" style="color:#2e7d32">${totalHadir}</div><div class="sum-lbl">Hari Hadir</div></div>
          <div class="sum-card"><div class="sum-val" style="color:#e65100">${totalIzin}</div><div class="sum-lbl">Izin</div></div>
          <div class="sum-card"><div class="sum-val" style="color:#c62828">${totalAlpha}</div><div class="sum-lbl">Alpha</div></div>
          <div class="sum-card"><div class="sum-val" style="color:#0b1829;font-size:16px">${totalDurasi}</div><div class="sum-lbl">Total Kerja</div></div>
        </div>

        <table>
          <thead><tr><th>Tgl</th><th>Hari</th><th>Masuk</th><th>Keluar</th><th>Durasi</th><th>Status</th></tr></thead>
          <tbody>${absenRows}</tbody>
        </table>

        ${laporanSection}

        <div style="margin-top:40px;border-top:1px solid #eee;padding-top:12px;font-size:10px;color:#aaa;text-align:center">
          Dokumen ini dibuat otomatis oleh sistem MIG Security · mig-security.id
        </div>
        </body></html>`;

      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.onload = () => { win.focus(); win.print(); };
    } catch (err) {
      console.error('Error generate PDF:', err);
      alert('Gagal generate PDF: ' + err.message);
    }
  };

  const bulanList = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2024, i).toLocaleDateString('id-ID', { month: 'long' })
  }));

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Rekap Absensi</h1>
          <p className="page-subtitle">Rekap kehadiran anggota per bulan</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {user.role === 'admin' && (
            <button className="btn btn-outline btn-sm" onClick={downloadCSV} disabled={rekap.length === 0}>
              📊 CSV
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={() => {
            const bulanNama = new Date(filter.tahun, filter.bulan - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
            const rows = rekap.map(r => `<tr><td>${r.name}</td><td style="text-align:center;color:#2e7d32">${r.total_masuk}</td><td style="text-align:center;color:#e65100">${r.total_izin}</td><td style="text-align:center;color:#c62828">${r.total_alpha}</td><td style="text-align:center">${r.total_keluar}</td></tr>`).join('');
            const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Rekap Absensi ${bulanNama}</title>
            <style>body{font-family:Arial,sans-serif;margin:30px;color:#1a1a1a}table{width:100%;border-collapse:collapse}th{background:#0b1829;color:#c9a84c;padding:8px;font-size:12px}td{padding:7px 8px;border-bottom:1px solid #eee;font-size:12px}tr:nth-child(even) td{background:#fafafa}.logo{font-size:22px;font-weight:900;margin-bottom:4px}.logo span{color:#c9a84c}@media print{body{margin:0}}</style></head>
            <body><div style="display:flex;justify-content:space-between;border-bottom:3px solid #c9a84c;padding-bottom:12px;margin-bottom:20px">
            <div><div class="logo"><span>MIG</span> Security</div><div style="font-size:11px;color:#888">PT. Muhammad Ibrahim Group</div></div>
            <div style="text-align:right"><div style="font-weight:700">Rekap Absensi ${bulanNama}</div><div style="font-size:11px;color:#888">Dicetak: ${new Date().toLocaleString('id-ID')}</div></div></div>
            <table><thead><tr><th>Nama</th><th>Masuk</th><th>Izin</th><th>Alpha</th><th>Keluar</th></tr></thead><tbody>${rows}</tbody></table>
            <div style="margin-top:12px;font-size:11px;color:#888">Total anggota: ${rekap.length}</div>
            </body></html>`;
            const win = window.open('', '_blank'); win.document.write(html); win.document.close(); win.onload = () => { win.focus(); win.print(); };
          }} disabled={rekap.length === 0}>
            🖨️ PDF
          </button>
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12 }} />
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="input" style={{ flex: 1, minWidth: 120 }} value={filter.bulan} onChange={e => setFilter(f => ({ ...f, bulan: e.target.value }))}>
          {bulanList.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <select className="input" style={{ width: 90 }} value={filter.tahun} onChange={e => setFilter(f => ({ ...f, tahun: e.target.value }))}>
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {wilayah.length > 0 && (
          <select className="input" style={{ flex: 1, minWidth: 120 }} value={filter.wilayah_id} onChange={e => setFilter(f => ({ ...f, wilayah_id: e.target.value }))}>
            <option value="">Semua Wilayah</option>
            {wilayah.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
          </select>
        )}
      </div>

      {/* Summary Cards */}
      {rekap.length > 0 && (
        <div className="grid-3" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-value">{rekap.length}</div>
            <div className="stat-label">Total Anggota</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>
              {rekap.reduce((a, r) => a + parseInt(r.total_masuk || 0), 0)}
            </div>
            <div className="stat-label">Total Masuk</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❌</div>
            <div className="stat-value" style={{ color: 'var(--red)' }}>
              {rekap.reduce((a, r) => a + parseInt(r.total_alpha || 0), 0)}
            </div>
            <div className="stat-label">Total Alpha</div>
          </div>
        </div>
      )}

      {/* List Rekap */}
      {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
        rekap.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <p>Belum ada data absensi bulan ini</p>
          </div>
        ) : rekap.map(r => (
          <div key={r.id} className="list-item" onClick={() => { setAbsenDetail(null); fetchDetail(r.id); }}>
            <div className="list-item-avatar" style={{ overflow: 'hidden' }}>
              {r.foto_url
                ? <img src={r.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : r.name?.charAt(0)}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{r.name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                <span className="badge badge-green">✅ {r.total_masuk} masuk</span>
                <span className="badge badge-yellow">📝 {r.total_izin} izin</span>
                <span className="badge badge-red">❌ {r.total_alpha} alpha</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)' }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            <button className="btn btn-outline btn-sm" style={{ fontSize: '0.65rem', padding: '3px 8px', whiteSpace: 'nowrap' }}
              onClick={e => { e.stopPropagation(); downloadLaporanAnggota(r); }}>
              🖨️ PDF
            </button>
          </div>
          </div>
        ))
      }

      {/* Modal detail absen per anggota */}
      {absenDetail !== null && (
        <div className="modal-overlay" onClick={() => setAbsenDetail(null)}>
          <div className="modal-sheet" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Detail Absensi</h2>
            <button onClick={() => setAbsenDetail(null)} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#8899aa', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>✕</button>
          </div>

            {/* Info anggota */}
            {absenDetail.length > 0 && (
              <div style={{ background: 'var(--navy-600)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{absenDetail[0]?.anggota_name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  📍 {absenDetail[0]?.wilayah_nama} · {absenDetail.length} catatan absensi
                </div>
                {/* Hitung durasi kerja hari ini */}
                {(() => {
                  const masuk = absenDetail.find(a => a.type === 'masuk');
                  const keluar = absenDetail.find(a => a.type === 'keluar');
                  if (masuk && keluar) {
                    const menit = Math.round((new Date(keluar.waktu) - new Date(masuk.waktu)) / 60000);
                    const jam = Math.floor(menit / 60); const sisa = menit % 60;
                    return <div style={{ fontSize: '0.75rem', color: 'var(--green)', marginTop: 4 }}>⏱ Durasi kerja: {jam}j {sisa}m</div>;
                  }
                  return null;
                })()}
              </div>
            )}

            {absenDetail.length === 0 ? (
              <div className="empty-state"><p>Tidak ada data absensi bulan ini</p></div>
            ) : absenDetail.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0', borderBottom: i < absenDetail.length - 1 ? '1px solid var(--border)' : 'none' }}>
                {a.foto_url && (
                  <img src={a.foto_url} alt="" onClick={() => setLightbox(a.foto_url)}
                    style={{ width: 64, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: a.type === 'masuk' ? 'var(--green)' : a.type === 'keluar' ? 'var(--red)' : a.type === 'izin' ? 'var(--yellow)' : 'var(--text-muted)' }}>
                      {a.type === 'masuk' ? '✅' : a.type === 'keluar' ? '🚪' : a.type === 'izin' ? '📝' : '❌'} {a.type.charAt(0).toUpperCase() + a.type.slice(1)}
                    </div>
                    <div style={{ fontSize: '0.78rem', fontFamily: 'JetBrains Mono', color: 'var(--text-secondary)', fontWeight: 600 }}>
                      {formatDateTime(a.waktu)}
                    </div>
                  </div>
                  {a.wilayah_nama && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 2 }}>📍 {a.wilayah_nama}</div>}
                  {a.keterangan && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 2 }}>📝 {a.keterangan}</div>}
                  {a.lat && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    GPS: {parseFloat(a.lat).toFixed(6)}, {parseFloat(a.lng).toFixed(6)}
                  </div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FORM ABSEN (Anggota) ──────────────────────────────────────────────────
function FormAbsen({ user }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [absenHariIni, setAbsenHariIni] = useState([]);
  const [step, setStep] = useState('idle');
  const [type, setType] = useState('');
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [coords, setCoords] = useState(null);
  const [keterangan, setKeterangan] = useState('');
  const [wilayahInfo, setWilayahInfo] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchAbsen(); fetchWilayah(); }, []);

  const fetchAbsen = async () => {
    try { setAbsenHariIni(await api.get('/absensi')); } catch {}
  };

  const fetchWilayah = async () => {
    try {
      const data = await api.get('/wilayah');
      if (data?.length > 0) {
        setWilayahInfo(data.find(w => w.id === user.wilayah_id) || data[0]);
      }
    } catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GPS tidak tersedia')); return; }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => reject(new Error('GPS gagal — aktifkan izin lokasi')),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  });

  const startAbsen = async (absenType) => {
    setType(absenType);
    setStep('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { setStep('upload'); }
  };

  const capturePhoto = async () => {
    let loc = null;
    try { loc = await getLocation(); setCoords(loc); } catch {}
    const canvas = addOverlayToCanvas(videoRef.current, loc?.lat, loc?.lng);
    canvas.toBlob(blob => {
      setFoto(blob);
      setFotoPreview(URL.createObjectURL(blob));
      stopCamera();
      setStep('preview');
    }, 'image/jpeg', 0.9);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    let loc = null;
    try { loc = await getLocation(); setCoords(loc); } catch {}
    const img = new Image();
    img.onload = () => {
      const canvas = addOverlayToCanvas(img, loc?.lat, loc?.lng);
      canvas.toBlob(blob => {
        setFoto(blob);
        setFotoPreview(URL.createObjectURL(blob));
        setStep('preview');
      }, 'image/jpeg', 0.9);
    };
    img.src = URL.createObjectURL(file);
  };

  const stopCamera = () => { streamRef.current?.getTracks().forEach(t => t.stop()); };

  const submitAbsen = async () => {
    setStep('submitting');
    try {
      let loc = coords;
      if (!loc && ['masuk', 'keluar'].includes(type)) {
        try { loc = await getLocation(); setCoords(loc); }
        catch (err) { setStep('preview'); showMsg(err.message, 'error'); return; }
      }
      if (type === 'masuk' && loc && wilayahInfo?.lat && wilayahInfo?.lng && wilayahInfo?.radius_meter) {
        const jarak = hitungJarak(loc.lat, loc.lng, parseFloat(wilayahInfo.lat), parseFloat(wilayahInfo.lng));
        if (jarak > wilayahInfo.radius_meter) {
          setStep('preview');
          showMsg(`❌ Kamu ${Math.round(jarak)}m dari wilayah. Radius: ${wilayahInfo.radius_meter}m`, 'error');
          return;
        }
      }
      const fd = new FormData();
      fd.append('type', type);
      if (loc) { fd.append('lat', loc.lat); fd.append('lng', loc.lng); }
      fd.append('keterangan', keterangan);
      if (foto) fd.append('foto', foto, 'absen.jpg');
      await api.postForm('/absensi', fd);
      showMsg(`✅ Absen ${type} berhasil!`);
      setStep('idle'); setFoto(null); setFotoPreview(null); setKeterangan(''); setCoords(null);
      fetchAbsen();
    } catch (err) { showMsg(err.message, 'error'); setStep('preview'); }
  };

  const submitKetidakhadiran = async (absenType) => {
    if (!keterangan.trim()) { showMsg('Keterangan wajib diisi', 'error'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('type', absenType);
      fd.append('keterangan', keterangan);
      await api.postForm('/absensi', fd);
      showMsg(`✅ ${absenType === 'izin' ? 'Izin' : 'Tidak masuk'} berhasil dicatat`);
      setKeterangan('');
      fetchAbsen();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const cancel = () => { stopCamera(); setStep('idle'); setFoto(null); setFotoPreview(null); setKeterangan(''); setCoords(null); };
  const sudahMasuk = absenHariIni.some(a => a.type === 'masuk');
  const sudahKeluar = absenHariIni.some(a => a.type === 'keluar');
  const sudahIzin = absenHariIni.some(a => a.type === 'izin' || a.type === 'alpha');

  return (
    <div className="page fade-in">
      <h1 className="page-title">Absensi</h1>
      <p className="page-subtitle">{new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {wilayahInfo && (
        <div style={{ background: 'var(--navy-700)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>📍</span>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{wilayahInfo.nama}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              Radius: {wilayahInfo.radius_meter ? `${wilayahInfo.radius_meter}m` : 'Belum diset'}
            </div>
          </div>
        </div>
      )}

      {absenHariIni.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>Riwayat Hari Ini</div>
          {absenHariIni.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < absenHariIni.length - 1 ? '1px solid var(--border)' : 'none' }}>
              {a.foto_url && <img src={a.foto_url} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'capitalize', color: a.type === 'masuk' ? 'var(--green)' : a.type === 'keluar' ? 'var(--red)' : 'var(--yellow)' }}>
                  {a.type === 'masuk' ? '✅' : a.type === 'keluar' ? '🚪' : '📝'} {a.type}
                </div>
                {a.keterangan && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{a.keterangan}</div>}
              </div>
              <div style={{ fontSize: '0.8rem', fontFamily: 'JetBrains Mono', color: 'var(--text-secondary)' }}>{formatTime(a.waktu)}</div>
            </div>
          ))}
        </div>
      )}

      {step === 'camera' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--yellow)', marginBottom: 10, textAlign: 'center' }}>📍 GPS + timestamp otomatis di-overlay ke foto</div>
          <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 12, background: '#000', aspectRatio: '4/3', position: 'relative' }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost btn-full" onClick={cancel}>Batal</button>
            <button className="btn btn-gold btn-full" onClick={capturePhoto}>📸 Capture + GPS</button>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
            Hanya dari kamera — untuk keabsahan absensi
          </div>
        </div>
      )}

      {step === 'upload' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>📵</div>
            <p style={{ fontSize: '0.875rem', color: 'var(--red)', fontWeight: 600 }}>Kamera tidak tersedia</p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>Absensi hanya bisa dilakukan dengan kamera untuk keabsahan data. Pastikan izin kamera sudah diaktifkan.</p>
          </div>
          <button className="btn btn-ghost btn-full" style={{ marginTop: 8 }} onClick={cancel}>Tutup</button>
        </div>
      )}

      {step === 'preview' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <img src={fotoPreview} alt="preview" style={{ width: '100%', borderRadius: 12, marginBottom: 12, objectFit: 'cover' }} />
          {coords && <div style={{ background: 'var(--green-soft)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem', color: 'var(--green)', marginBottom: 12 }}>✅ GPS: {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</div>}
          <div className="input-group">
            <input className="input" placeholder="Keterangan (opsional)" value={keterangan} onChange={e => setKeterangan(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost btn-full" onClick={() => setStep('camera')}>↩ Ulang</button>
            <button className={`btn btn-full ${type === 'masuk' ? 'btn-gold' : 'btn-danger'}`} onClick={submitAbsen}>✓ Absen {type}</button>
          </div>
        </div>
      )}

      {step === 'submitting' && <div className="loading-center" style={{ flexDirection: 'column', gap: 12 }}><div className="loading-spinner" /><p style={{ fontSize: '0.875rem' }}>Memvalidasi & menyimpan...</p></div>}

      {step === 'idle' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            <button className="absen-btn absen-masuk" onClick={() => startAbsen('masuk')} disabled={sudahMasuk} style={{ opacity: sudahMasuk ? 0.4 : 1 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              {sudahMasuk ? '✓ Sudah Absen Masuk' : 'Absen Masuk'}
            </button>
            <button className="absen-btn absen-keluar" onClick={() => startAbsen('keluar')} disabled={!sudahMasuk || sudahKeluar} style={{ opacity: (!sudahMasuk || sudahKeluar) ? 0.4 : 1 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {sudahKeluar ? '✓ Sudah Absen Keluar' : 'Absen Keluar'}
            </button>
          </div>
          {!sudahIzin && !sudahMasuk && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 4 }}>📝 Lapor Ketidakhadiran</div>
              <p style={{ fontSize: '0.78rem', marginBottom: 12 }}>Tanpa GPS dan foto</p>
              <div className="input-group">
                <textarea className="input" rows={3} placeholder="Keterangan (sakit, keperluan keluarga, dll)..." value={keterangan} onChange={e => setKeterangan(e.target.value)} style={{ resize: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-outline btn-full" onClick={() => submitKetidakhadiran('izin')} disabled={loading}>{loading ? '...' : '📋 Izin'}</button>
                <button className="btn btn-danger btn-full" onClick={() => submitKetidakhadiran('alpha')} disabled={loading}>{loading ? '...' : '❌ Tidak Masuk'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── MAIN EXPORT ───────────────────────────────────────────────────────────
export default function AbsenPage({ user }) {
  if (user.role === 'admin' || user.role === 'spv') {
    return <RekapAbsensi user={user} />;
  }
  return <FormAbsen user={user} />;
}
